import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix, getBrandLocale } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import VentesListPageChrome, { facturationTableScrollClass } from './components/VentesListPageChrome';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { TrendingUp, Search, Plus, Edit, Trash2, RefreshCcw, Filter, Eye, X, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, LayoutGrid, Target, DollarSign, Calendar, Clock, List, LayoutList, Check, BarChart3, Package, Percent, AlertCircle, CheckCircle, FileText, Layers } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import {
  useCrudModalBodyLock,
  initVentesColumnVisibility,
  buildVentesQawaemColumnDefs,
  isVentesColumnVisible,
  mapVentesStatCards,
  buildVentesBulkDeleteFilterAction,
  computeVentesRowSelection,
  sliceVentesListItems,
  matchesVentesListDateRange,
} from './ventesListPageUtils';
import { useVentesConfig } from './useVentesConfig';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const PrevisionsVentesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.ventes.pages.previsionsVentesPage.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const dateLocale = getBrandLocale(i18n.language);
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(dateLocale) : '-');
  const periodLabel = (type) => ({ monthly: p('period_monthly'), quarterly: p('period_quarterly'), yearly: p('period_yearly') }[type] || type);

  const [searchTerm, setSearchTerm] = useState('');
  const [forecasts, setForecasts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const ventesConfig = useVentesConfig();
  const { dateRange } = useSaudiListDateRange();

  useEffect(() => {
    setViewMode(ventesConfig.defaultView || 'table');
  }, [ventesConfig.defaultView]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  useCrudModalBodyLock(showModal, showViewModal);
  
  // Form tab state
  const [formTab, setFormTab] = useState('basic');
  
  const [formData, setFormData] = useState({ 
    forecast_name: '', 
    period_type: 'monthly',
    start_date: '',
    end_date: '',
    product_category: '',
    expected_quantity: '',
    expected_revenue: '',
    confidence_level: '',
    status: 'draft',
    actual_quantity: '',
    actual_revenue: '',
    variance_percentage: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_forecasts');
  const [quickFilter, setQuickFilter] = useState('all');
  
  // PDF and Excel export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  
  const forecastFormTabs = useMemo(() => ([
    { id: 'basic', label: p('section_basic'), icon: BarChart3, iconColor: 'text-emerald-400' },
    { id: 'results', label: p('section_actual_data'), icon: CheckCircle, iconColor: 'text-green-400' },
  ]), [p]);

  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const thFilterClass = 'text-white font-semibold py-4';

  const tableColumnDefs = useMemo(
    () => [
      { key: 'forecast_name', label: p('col_forecast'), required: true },
      { key: 'period_type', label: p('col_period') },
      { key: 'expected_revenue', label: p('label_expected_revenue') },
      { key: 'confidence_level', label: p('col_confidence') },
      { key: 'start_date', label: p('col_start_date') },
      { key: 'status', label: p('col_status') },
    ],
    [p],
  );
  const [columnVisibility, setColumnVisibility] = useState(() =>
    initVentesColumnVisibility(tableColumnDefs.map((c) => c.key)),
  );
  const qawaemColumnDefs = useMemo(
    () => buildVentesQawaemColumnDefs(tableColumnDefs, columnVisibility),
    [tableColumnDefs, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const showCol = (key) => isVentesColumnVisible(columnVisibility, key);
  const tableColSpan = tableColumnDefs.filter((c) => showCol(c.key)).length + 2;

  useEffect(() => { loadForecasts(); }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadForecasts = async () => {
    try {
      const response = await localApiAdapter.get('/sales/forecasts');
      const rows = response?.data || response || [];
      setForecasts(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading forecasts:', error);
      setForecasts([]);
    }
  };

  const stats = {
    total: forecasts.length,
    draft: forecasts.filter(f => f.status === 'draft').length,
    approved: forecasts.filter(f => f.status === 'approved').length,
    completed: forecasts.filter(f => f.status === 'completed').length,
    totalExpectedRevenue: forecasts.reduce((sum, f) => sum + (parseFloat(f.expected_revenue) || 0), 0),
    totalActualRevenue: forecasts.filter(f => f.status === 'completed').reduce((sum, f) => sum + (parseFloat(f.actual_revenue) || 0), 0)
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_forecasts', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'by_period', name: p('tab_by_period'), icon: Calendar },
    { id: 'by_status', name: t('common.by_status'), icon: CheckCircle },
  ]), [t, p]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: t('common.all'), icon: Layers },
    { id: 'draft', name: p('filter_draft'), icon: FileText },
    { id: 'approved', name: p('filter_approved'), icon: CheckCircle },
    { id: 'completed', name: p('filter_completed'), icon: Check },
  ]), [t, p]);

  const forecastDistribution = useMemo(() => ({
    all_forecasts: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'draft', name: p('dist_draft'), count: stats.draft, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
        { filterKey: 'approved', name: p('dist_approved'), count: stats.approved, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'completed', name: p('dist_completed'), count: stats.completed, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    by_period: {
      title: p('dist_by_period_title'),
      data: [
        { filterKey: 'monthly', name: p('dist_monthly'), count: forecasts.filter(f => f.period_type === 'monthly').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'quarterly', name: p('dist_quarterly'), count: forecasts.filter(f => f.period_type === 'quarterly').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'yearly', name: p('dist_yearly'), count: forecasts.filter(f => f.period_type === 'yearly').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    by_status: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'in_progress', name: p('dist_in_progress'), count: stats.draft + stats.approved, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'completed_plural', name: p('dist_completed_plural'), count: stats.completed, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
  }), [p, stats, forecasts]);

  const distributionInfo = forecastDistribution[mainViewMode] || forecastDistribution.all_forecasts;

  const forecastStatusFilterOptions = useMemo(() => ([
    { value: 'draft', label: p('filter_draft') },
    { value: 'approved', label: p('filter_approved') },
    { value: 'completed', label: p('filter_completed') },
  ]), [p]);

  const columnFilterRules = useMemo(() => ([
    textColumnRule('forecast_name'),
    textColumnRule('period_type', (r) => periodLabel(r.period_type)),
    textColumnRule('expected_revenue', (r) => String(r.expected_revenue ?? '')),
    textColumnRule('confidence_level', (r) => String(r.confidence_level ?? '')),
    textColumnRule('start_date', (r) => (r.start_date ? fmtDate(r.start_date) : '')),
    exactColumnRule('status', (r) => r.status ?? ''),
  ]), [p]);

  const baseFilteredForecasts = useMemo(() => forecasts.filter(f => {
    if (!matchesVentesListDateRange(f.start_date, dateRange)) return false;
    
    if (statusFilter !== 'all' && f.status !== statusFilter) return false;
    
    if (searchTerm && !f.forecast_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_forecasts') {
      if (quickFilter === 'draft' && f.status !== 'draft') return false;
      if (quickFilter === 'approved' && f.status !== 'approved') return false;
      if (quickFilter === 'completed' && f.status !== 'completed') return false;
    }
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_forecasts':
          if (activeDistributionFilter === 'draft' && f.status !== 'draft') return false;
          if (activeDistributionFilter === 'approved' && f.status !== 'approved') return false;
          if (activeDistributionFilter === 'completed' && f.status !== 'completed') return false;
          break;
        case 'by_period':
          if (activeDistributionFilter === 'monthly' && f.period_type !== 'monthly') return false;
          if (activeDistributionFilter === 'quarterly' && f.period_type !== 'quarterly') return false;
          if (activeDistributionFilter === 'yearly' && f.period_type !== 'yearly') return false;
          break;
        case 'by_status':
          if (activeDistributionFilter === 'in_progress' && !['draft', 'approved'].includes(f.status)) return false;
          if (activeDistributionFilter === 'completed_plural' && f.status !== 'completed') return false;
          break;
      }
    }
    
    return true;
  }), [forecasts, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, statusFilter, dateRange]);

  const filteredForecasts = useMemo(
    () => applyColumnFilters(baseFilteredForecasts, filters, columnFilterRules),
    [baseFilteredForecasts, filters, columnFilterRules],
  );

  const filteredForecastIds = useMemo(() => filteredForecasts.map((r) => r.id), [filteredForecasts]);
  const displayedForecasts = useMemo(
    () => sliceVentesListItems(filteredForecasts, ventesConfig.itemsPerPage),
    [filteredForecasts, ventesConfig.itemsPerPage],
  );
  const { allRowsSelected, someRowsSelected } = useMemo(
    () => computeVentesRowSelection(filteredForecastIds, selectedRecords),
    [filteredForecastIds, selectedRecords],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { 
        forecast_name: formData.forecast_name,
        period_type: formData.period_type,
        start_date: formData.start_date,
        end_date: formData.end_date,
        product_category: formData.product_category || '',
        expected_quantity: parseFloat(formData.expected_quantity) || 0,
        expected_revenue: parseFloat(formData.expected_revenue) || 0,
        confidence_level: parseFloat(formData.confidence_level) || 0,
        status: formData.status,
        actual_quantity: parseFloat(formData.actual_quantity) || 0,
        actual_revenue: parseFloat(formData.actual_revenue) || 0,
        variance_percentage: parseFloat(formData.variance_percentage) || 0,
        notes: formData.notes || ''
      };
      
      if (editingRecord) {
        await localApiAdapter.put(`/sales/forecasts/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'توقع مبيعات' }));
      } else {
        await localApiAdapter.post('/sales/forecasts', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'توقع مبيعات' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('unknown_error') }));
      return;
    } finally {
      resetForm();
      loadForecasts();
    }
  };

  const handleEdit = (record) => {
    setFormTab('basic');
    setEditingRecord(record);
    setFormData({ 
      forecast_name: record.forecast_name || '',
      period_type: record.period_type || 'monthly',
      start_date: record.start_date ? record.start_date.substring(0, 10) : '',
      end_date: record.end_date ? record.end_date.substring(0, 10) : '',
      product_category: record.product_category || '',
      expected_quantity: record.expected_quantity?.toString() || '',
      expected_revenue: record.expected_revenue?.toString() || '',
      confidence_level: record.confidence_level?.toString() || '',
      status: record.status || 'draft',
      actual_quantity: record.actual_quantity?.toString() || '',
      actual_revenue: record.actual_revenue?.toString() || '',
      variance_percentage: record.variance_percentage?.toString() || '',
      notes: record.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/sales/forecasts/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'توقع مبيعات' }));
        loadForecasts();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_delete_error', { defaultValue: 'تعذّر حذف التوقع' }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/sales/forecasts/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'توقع مبيعات' }));
        setSelectedRecords([]);
        loadForecasts();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_bulk_delete_error', { defaultValue: 'تعذّر حذف التوقعات المحددة' }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      forecast_name: '', 
      period_type: 'monthly',
      start_date: '',
      end_date: '',
      product_category: '',
      expected_quantity: '',
      expected_revenue: '',
      confidence_level: '',
      status: 'draft',
      actual_quantity: '',
      actual_revenue: '',
      variance_percentage: '',
      notes: ''
    });
    setEditingRecord(null);
    setFormTab('basic');
    setShowModal(false);
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredForecastIds);
    }
  };
  
  const handleSelectRecord = (id) => { 
    setSelectedRecords(selectedRecords.includes(id) ? selectedRecords.filter(rId => rId !== id) : [...selectedRecords, id]); 
  };

  const statusOptions = useMemo(() => ([
    { value: 'draft', label: p('status_draft'), icon: FileText, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
    { value: 'approved', label: p('status_approved'), icon: CheckCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'completed', label: p('status_completed'), icon: Check, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: X, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ]), [p]);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/sales/forecasts/${recordId}`, { status: newStatus });
      await loadForecasts();
    } catch (error) {
      console.error('❌ Error updating status:', error);
    }
  };

  const listStats = useMemo(
    () => mapVentesStatCards([
      { key: 'total', label: p('stat_total'), value: stats.total, icon: BarChart3, variant: 'copper' },
      { key: 'draft', label: p('stat_draft'), value: stats.draft, icon: FileText, variant: 'forest' },
      { key: 'approved', label: p('stat_approved'), value: stats.approved, icon: CheckCircle, variant: 'copper' },
      { key: 'completed', label: p('stat_completed'), value: stats.completed, icon: Check, variant: 'forest' },
    ]),
    [stats, p],
  );

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
              <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      {mainViewMode === 'all_forecasts' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button key={filter.id} type="button" onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
          {activeDistributionFilter && (
            <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
              <X size={14} />{p('reset_filter')}
            </button>
          )}
        </div>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>
          {distributionInfo.data.map((item) => (
            <button key={item.filterKey || item.name} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`} title={p('filter_by_title', { name: item.name })}>
              <div className="flex justify-between items-center mb-2">
                <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}%` }} />
              </div>
              <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>{(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}% {t('common.of_total')}</p>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <>
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
      <VentesListPageChrome
        t={t}
        p={p}
        qawaem={qawaem}
        isDashboardLightMode={isDashboardLightMode}
        title={p('title')}
        subtitle={p('subtitle')}
        icon={BarChart3}
        listStats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildVentesBulkDeleteFilterAction({
          selectedCount: selectedRecords.length,
          onDelete: handleBulkDelete,
          label: p('bulk_delete_confirm', { count: selectedRecords.length }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search') })}
        onCreate={() => { setEditingRecord(null); setFormTab('basic'); setShowModal(true); }}
        createLabel={p('new_forecast')}
        importExportState={{ showImportDropdown, setShowImportDropdown, showExportDropdown, setShowExportDropdown, importDropdownRef, exportDropdownRef }}
        importExportHandlers={{}}
        isGeneratingPDF={isGeneratingPDF}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        extraHeaderActions={(
          <button type="button" onClick={loadForecasts} className="refresh-button" title={t('common.refresh')}>
            <RefreshCcw size={20} />
          </button>
        )}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox size="lg" checked={allRowsSelected} indeterminate={someRowsSelected} onChange={handleSelectAll} title={allRowsSelected ? p('deselect_all', { defaultValue: t('common.deselect_all') }) : p('select_all', { defaultValue: t('common.select_all') })} /></div></th>
                  
                  <TableColumnHeaderFilter columnKey="forecast_name" label={p('col_forecast')} value={filters.forecast_name || ''} onChange={(v) => setFilter('forecast_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  {showCol('period_type') ? (
                    <TableColumnHeaderFilter columnKey="period_type" label={p('col_period')} value={filters.period_type || ''} onChange={(v) => setFilter('period_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('expected_revenue') ? (
                    <TableColumnHeaderFilter columnKey="expected_revenue" label={p('label_expected_revenue')} value={filters.expected_revenue || ''} onChange={(v) => setFilter('expected_revenue', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('confidence_level') ? (
                    <TableColumnHeaderFilter columnKey="confidence_level" label={p('col_confidence')} value={filters.confidence_level || ''} onChange={(v) => setFilter('confidence_level', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('start_date') ? (
                    <TableColumnHeaderFilter columnKey="start_date" label={p('col_start_date')} value={filters.start_date || ''} onChange={(v) => setFilter('start_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('status') ? (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={forecastStatusFilterOptions} value={filters.status || ''} onChange={(v) => setFilter('status', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedForecasts.length > 0 ? (
                  displayedForecasts.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} /></div></td>
                      
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.forecast_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.product_category || p('ph_category')}</span>
                        </div>
                      </td>
                      {showCol('period_type') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{periodLabel(record.period_type)}</span>
                            <span className="text-white/50 text-xs mt-0.5">
                              {fmtDate(record.start_date)} - {fmtDate(record.end_date)}
                            </span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('expected_revenue') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{formatBrandMoney(record.expected_revenue || 0)}</span>
                            <span className="text-white/50 text-xs mt-0.5">{record.expected_quantity || 0} {p('units_suffix')}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('confidence_level') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.confidence_level || 0}%</span>
                            <span className="text-white/50 text-xs mt-0.5">{p('label_confidence_sub')}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('start_date') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Clock size={14} className="text-white/50" />
                              {fmtDate(record.start_date)}
                            </div>
                            <span className="text-white/50 text-xs mt-0.5">{p('label_start_date_sub')}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={record.status}
                              recordId={record.id}
                              options={statusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status')}
                            />
                          </div>
                        </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><BarChart3 size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedForecasts.length > 0 ? (
              displayedForecasts.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <TrendingUp size={20} className="text-emerald-400" />
                          {record.forecast_name}
                        </h3>
                        <QawaemClickableStatusBadge
                          status={record.status}
                          recordId={record.id}
                          options={statusOptions}
                          onChange={handleStatusChange}
                          title={p('click_change_status')}
                        />
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                          {formatBrandMoney(record.expected_revenue || 0)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_general')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_period')}</span> {periodLabel(record.period_type)}</div>
                            <div><span className="font-medium">{p('label_category')}</span> {record.product_category || p('label_all_categories')}</div>
                            <div><span className="font-medium">{p('label_start')}:</span> {fmtDate(record.start_date)}</div>
                            <div><span className="font-medium">{p('label_end')}:</span> {fmtDate(record.end_date)}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_forecast')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <DollarSign size={16} className="text-green-400" />
                              <span className="font-medium">{p('label_revenue')}</span> <span className="text-white">{formatBrandMoney(record.expected_revenue || 0)}</span>
                            </div>
                            <div><span className="font-medium">{p('label_quantity')}</span> {record.expected_quantity || 0} {p('units_suffix')}</div>
                            <div><span className="font-medium">{p('label_confidence')}</span> {record.confidence_level || 0}%</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_actual')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_actual_revenue')}</span> {formatBrandMoney(record.actual_revenue || 0)}</div>
                            <div><span className="font-medium">{p('label_actual_qty')}</span> {record.actual_quantity || 0} {p('units_suffix')}</div>
                            <div><span className="font-medium">{p('label_variance')}</span> {record.variance_percentage || 0}%</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_notes')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <p>{record.notes || p('no_notes')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <BarChart3 size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
      </VentesListPageChrome>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_title')}
        icon={Eye}
        maxWidth="1100px"
        testId="previsions-view-modal"
      >
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 size={18} className="text-emerald-400" />
                    {p('section_general')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_forecast')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.forecast_name}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_period_type')}</p>
                      <p className="text-white font-medium">{periodLabel(viewingRecord.period_type)}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_category')}</p>
                      <p className="text-white font-medium">{viewingRecord.product_category || p('label_all_categories')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_status')}</p>
                      <QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Target size={18} className="text-blue-400" />
                      {p('section_forecast')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_expected_revenue')}</p>
                        <p className="text-white font-medium">{formatBrandMoney(viewingRecord.expected_revenue || 0)}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_expected_qty')}</p>
                        <p className="text-white font-medium">{viewingRecord.expected_quantity || 0} {p('units_suffix')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_confidence_level')}</p>
                        <p className="text-white font-medium">{viewingRecord.confidence_level || 0}%</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <CheckCircle size={18} className="text-green-400" />
                      {p('section_actual')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_actual_revenue_field')}</p>
                        <p className="text-white font-medium">{formatBrandMoney(viewingRecord.actual_revenue || 0)}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_actual_quantity')}</p>
                        <p className="text-white font-medium">{viewingRecord.actual_quantity || 0} {p('units_suffix')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_variance_pct')}</p>
                        <p className="text-white font-medium">{viewingRecord.variance_percentage || 0}%</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <FileText size={18} className="text-purple-400" />
                    {p('section_notes')}
                  </h3>
                  <p className="text-white/80">{viewingRecord.notes || p('no_notes')}</p>
                </div>
              </div>
      </QawaemErpViewModal>
      ) : null}

      <QawaemErpCrudModal
        open={showModal}
        onClose={resetForm}
        title={editingRecord ? p('modal_edit') : p('modal_create')}
        icon={TrendingUp}
        showSaveButton
        formId="forecast-form"
        saveLabel={editingRecord ? p('update') : p('create')}
        testId="previsions-crud-modal"
        tabs={forecastFormTabs}
        activeTab={formTab}
        onTabChange={setFormTab}
        tabsAriaLabel={editingRecord ? p('modal_edit') : p('modal_create')}
      >
        <form id="forecast-form" onSubmit={handleSubmit}>
          {formTab === 'basic' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_forecast_name', { defaultValue: p('col_forecast') })} <span className="qawaem-required-mark">*</span></label>
                <input type="text" required value={formData.forecast_name} onChange={(e) => setFormData({ ...formData, forecast_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_forecast')} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_period_type')}</label>
                <CustomSelect value={formData.period_type} onChange={(value) => setFormData({ ...formData, period_type: value })} options={[{ value: 'monthly', label: p('period_monthly') }, { value: 'quarterly', label: p('period_quarterly') }, { value: 'yearly', label: p('period_yearly') }]} placeholder={p('ph_period')} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_start_date')} <span className="qawaem-required-mark">*</span></label>
                <CustomDatePicker value={formData.start_date} onChange={(date) => setFormData({ ...formData, start_date: date })} placeholder={p('ph_start_date')} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_end_date')}</label>
                <CustomDatePicker value={formData.end_date} onChange={(date) => setFormData({ ...formData, end_date: date })} placeholder={p('ph_start_date')} minDate={formData.start_date} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_category')}</label>
                <input type="text" value={formData.product_category} onChange={(e) => setFormData({ ...formData, product_category: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_category_examples')} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_status')}</label>
                <CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={statusOptions.map(s => ({ value: s.value, label: s.label, icon: <s.icon size={16} /> }))} placeholder={p('ph_status')} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_expected_qty')}</label>
                <CustomNumberInput value={formData.expected_quantity} onChange={(value) => setFormData({ ...formData, expected_quantity: value })} placeholder={p('placeholder_zero')} step={1} min={0} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_expected_revenue')}</label>
                <CustomNumberInput value={formData.expected_revenue} onChange={(value) => setFormData({ ...formData, expected_revenue: value })} placeholder={p('placeholder_zero')} step={1000} min={0} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_confidence')}</label>
                <CustomNumberInput value={formData.confidence_level} onChange={(value) => setFormData({ ...formData, confidence_level: value })} placeholder={p('placeholder_zero')} step={5} min={0} max={100} />
              </div>
            </div>
          )}
          {formTab === 'results' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_actual_qty')}</label>
                <CustomNumberInput value={formData.actual_quantity} onChange={(value) => setFormData({ ...formData, actual_quantity: value })} placeholder={p('placeholder_zero')} step={1} min={0} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_actual_revenue')}</label>
                <CustomNumberInput value={formData.actual_revenue} onChange={(value) => setFormData({ ...formData, actual_revenue: value })} placeholder={p('placeholder_zero')} step={1000} min={0} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_variance')}</label>
                <CustomNumberInput value={formData.variance_percentage} onChange={(value) => setFormData({ ...formData, variance_percentage: value })} placeholder={p('placeholder_zero')} step={1} />
              </div>
              <div className="md:col-span-3">
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes_form')}</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_notes')} />
              </div>
            </div>
          )}
        </form>
      </QawaemErpCrudModal>
    </>
  );
};

export default PrevisionsVentesPage;

